// src/pages/VehicleDetail.jsx
import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../services/api'
import './VehicleDetail.css'
import LaudoIllustration from '../assets/illustrations/laudo.PNG'
import VideoPlayer from '../components/VideoPlayer'
import ShareButton from '../components/ShareButton';
import { FaAlignLeft } from 'react-icons/fa';
import FinancingSimulatorButton from "../components/FinancingSimulatorButton";
import VehicleCurationPanel from '../components/VehicleCurationPanel';
import { formatCurrency, formatPercent } from '../utils/format';
import usePageMeta from '../hooks/usePageMeta';
import { buildWhatsAppLink } from '../constants/connect';

// ícones react-icons
import {
  FaCalendarAlt,
  FaTachometerAlt,
  FaGasPump,
  FaCog,
  FaTools,
  FaHashtag,
  FaDoorOpen,
  FaPalette,
  FaCarSide,
  FaRegCheckCircle
} from 'react-icons/fa'
import { IconContext } from 'react-icons'

export default function VehicleDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [vehicle, setVehicle] = useState(null)
  const [current, setCurrent] = useState(0)

  // lightbox
  const [lightboxOpen,  setLightboxOpen]  = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const rawBase = process.env.REACT_APP_API_URL || '';
  const API_BASE = rawBase.replace(/\/api$/, '');

  useEffect(() => {
    api.get(`/vehicles/${id}`).then(r => {
      setVehicle(r.data);
    });
  }, [id]);

  const photos = Array.isArray(vehicle?.photos) ? vehicle.photos : []
  const coverPhoto = photos.find(p => p.isCover) || photos[0]
  const mainPhotoUrl = coverPhoto ? `${API_BASE}/uploads/${coverPhoto.filename}` : undefined
  const canonicalUrl = typeof window !== 'undefined' ? window.location.href : undefined

  const price = Number(vehicle?.price)
  const fipeValue = Number(vehicle?.fipePrice)
  const hasPrice = Number.isFinite(price)
  const hasFipePrice = Number.isFinite(fipeValue) && fipeValue > 0 && hasPrice
  const diff = hasFipePrice ? price - fipeValue : null
  const diffAbs = diff !== null ? Math.abs(diff) : null
  const percentDiff = diffAbs !== null && fipeValue > 0 ? (diffAbs / fipeValue) * 100 : null

  let fipeDiffClass = 'vd-fipe__diff vd-fipe__diff--neutral'
  let fipeDiffText = null
  if (hasFipePrice && percentDiff !== null) {
    if (diff < 0) {
      fipeDiffClass = 'vd-fipe__diff vd-fipe__diff--positive'
      fipeDiffText = `Economia de ${formatPercent(percentDiff)} (${formatCurrency(diffAbs)})`
    } else if (diff > 0) {
      fipeDiffClass = 'vd-fipe__diff vd-fipe__diff--negative'
      fipeDiffText = `Acima da FIPE ${formatPercent(percentDiff)} (${formatCurrency(diffAbs)})`
    } else {
      fipeDiffText = 'Valor alinhado a FIPE'
    }
  }

  const inventoryType = (vehicle?.inventoryType || '').toLowerCase();
  const inventoryLabel = inventoryType === 'estoque'
    ? 'Estoque Connect'
    : inventoryType === 'locadora'
      ? 'Locadora parceira'
      : 'Disponivel';

  const metaTitle = vehicle
    ? `${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.year} | Connect Veiculos`
    : 'Detalhes do veiculo | Connect Veiculos'
  const kmValue = Number(vehicle?.km)
  const kmText = Number.isFinite(kmValue) ? kmValue.toLocaleString('pt-BR') : ''
  const metaDescription = vehicle
    ? `Veja ${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.year}${kmText ? ` com ${kmText} km` : ''}, motor ${vehicle.engine} e ${vehicle.transmission} em Osasco.`
    : 'Confira os detalhes dos veiculos disponiveis na Connect Veiculos.'

  usePageMeta({
    title: metaTitle,
    description: metaDescription,
    image: mainPhotoUrl,
    url: canonicalUrl,
    type: 'vehicle',
  })

  const vehicleJsonLd = useMemo(() => {
    if (!vehicle) return null

    const images = photos.map((photo) => `${API_BASE}/uploads/${photo.filename}`)
    const json = {
      '@context': 'https://schema.org',
      '@type': 'Vehicle',
      name: `${vehicle.brand} ${vehicle.model} ${vehicle.version}`,
      brand: vehicle.brand,
      model: vehicle.model,
      vehicleModelDate: vehicle.year ? String(vehicle.year) : undefined,
      color: vehicle.color,
      vehicleTransmission: vehicle.transmission,
      fuelType: vehicle.fuelType,
      mileageFromOdometer: {
        '@type': 'QuantitativeValue',
        value: Number(vehicle.km) || 0,
        unitCode: 'KMT',
      },
      url: canonicalUrl,
      image: images,
    }

    if (hasPrice) {
      json.offers = {
        '@type': 'Offer',
        price: price,
        priceCurrency: 'BRL',
        availability: 'https://schema.org/InStock',
      }
    }

    if (!json.image || json.image.length === 0) {
      delete json.image
    }
    if (!json.vehicleModelDate) {
      delete json.vehicleModelDate
    }
    if (!json.url) {
      delete json.url
    }

    return JSON.stringify(json)
  }, [vehicle, photos, API_BASE, canonicalUrl, hasPrice, price])

  if (vehicle === null) {
    return <p>Carregando...</p>;
  }

  if (!vehicle.active) {
    return (
      <div className="vehicle-details">
        <h2>Anuncio nao disponivel</h2>
        <p>Este veiculo nao esta mais ativo.</p>
      </div>
    );
  }

  const total  = photos.length
  const kmDisplay = Number.isFinite(kmValue)
    ? `${kmValue.toLocaleString('pt-BR')} km`
    : (vehicle.km ?? 'KM indisponivel')

  const specs = [
    { Icon: FaCalendarAlt,     label: 'Ano',           value: vehicle.year },
    { Icon: FaTachometerAlt,   label: 'Km',            value: kmDisplay },
    { Icon: FaGasPump,         label: 'Combustivel',   value: vehicle.fuelType },
    { Icon: FaCog,             label: 'Transmissao',   value: vehicle.transmission },
    { Icon: FaTools,           label: 'Motor',         value: vehicle.engine },
    { Icon: FaHashtag,         label: 'Final placa',   value: vehicle.finalPlate },
    { Icon: FaDoorOpen,        label: 'Portas',        value: vehicle.doors },
    { Icon: FaPalette,         label: 'Cor',           value: vehicle.color },
    { Icon: FaCarSide,         label: 'Carroceria',    value: vehicle.bodyType },
  ]


  const prev     = () => setCurrent((current - 1 + total) % total)
  const next     = () => setCurrent((current + 1) % total)
  const goTo     = i => setCurrent(i)

  const openLightbox  = idx => { setLightboxIndex(idx); setLightboxOpen(true) }
  const closeLightbox = ()    => setLightboxOpen(false)
  const prevLight     = ()    => setLightboxIndex((lightboxIndex - 1 + total) % total);
  const nextLight     = ()    => setLightboxIndex((lightboxIndex + 1) % total);

function handleWhatsApp() {
  const { brand, model, version, year } = vehicle
  const lines = [
    `Tenho interesse no veiculo ${brand} ${model} ${version} (${year})`,
    canonicalUrl ? `Link: ${canonicalUrl}` : null,
  ].filter(Boolean)

  const url = buildWhatsAppLink(lines.join('\n'))
  window.open(url, '_blank', 'noopener')
}

  // ==== TESTE MANUAL: defina aqui um YouTube ID qualquer ====
  const manualTestId = 'cxQxQ2M_TqY'  // ex: Rick Astley :)

  function handleDownloadCautelar() {
  const link = document.createElement("a");
  link.href = `${API_BASE}/api/vehicles/${id}/reports/cautelar/download`;
  link.download = `laudo_cautelar_${vehicle.brand}_${vehicle.model}.pdf`; 
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function handleDownloadScanner() {
  const link = document.createElement("a");
  link.href = `${API_BASE}/api/vehicles/${id}/reports/scanner/download`;
  link.download = `laudo_complementar_${vehicle.brand}_${vehicle.model}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

  return (
    <div className="vehicle-detail">
      {vehicleJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: vehicleJsonLd }}
        />
      )}
      <button className="vd-back" onClick={() => navigate(-1)}>Voltar</button>

      <div className="vd-content">
        <div className="vd-left">
          {/* Carrossel */}
          <div className="carousel-container">
            <button className="arrow arrow-left"  onClick={prev}>‹</button>
            <img
              src={`${API_BASE}/uploads/${photos[current].filename}`}
              alt=""
              className="carousel-image"
              onClick={() => openLightbox(current)}
            />
            <button className="arrow arrow-right" onClick={next}>›</button>
          </div>
          <div className="carousel-dots">
            {photos.map((_, i) => (
              <span
                key={i}
                className={`dot${i === current ? ' active' : ''}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>

          
{lightboxOpen && (
  <div className="lightbox-overlay" onClick={closeLightbox}>
    {/* Botão de fechar */}
    <button
      className="lightbox-close"
      onClick={e => {
        e.stopPropagation();
        closeLightbox();
      }}
    >
      ✕
    </button>

    {/* Seta anterior */}
    <button
      className="lightbox-arrow left"
      onClick={e => {
        e.stopPropagation();
        prevLight();
      }}
    >
      ‹
    </button>

    {/* Imagem */}
    <img
      src={`${API_BASE}/uploads/${photos[lightboxIndex].filename}`}
      className="lightbox-image"
      onClick={e => e.stopPropagation()}
      alt=""
    />

    

    {/* Seta próxima */}
    <button
      className="lightbox-arrow right"
      onClick={e => {
        e.stopPropagation();
        nextLight();
      }}
    >
      ›
    </button>
  </div>
)}


<button
           className="btn-primary vd-action"
           onClick={handleWhatsApp}
>
           Tenho Interesse
          </button>

          {/* Laudo Cautelar */}
          <div className="laudo-section">
            <div className="laudo-content">
              <div className="laudo-text">
                <h3>
                  Laudo, o seu certificado&nbsp;
                  <span>qualidade e segurança.</span>
                </h3>
                <ul>
                  <li><FaRegCheckCircle className="check" /> Verificação de estrutura</li>
                  <li><FaRegCheckCircle className="check" /> Verificação de documentação</li>
                  <li><FaRegCheckCircle className="check" /> Debitos e Restrições</li>
                </ul>
                <button
                  className="laudo-btn"
                   /* </div>onClick={handleDownloadCautelar} */ 
                >
                    Laudo Cautelar Aprovado 100%
                </button>
                {/*
                <button
                  className="laudo-btn"
                  onClick={handleDownloadScanner}
                >
                 Baixar Laudo Complementar
                </button>
                */}
              </div>
              <div className="laudo-image">
                <img src={LaudoIllustration} alt="Ilustração do laudo cautelar" />
              </div>
            </div>


            
          </div>

          {/*<VehicleCurationPanel vehicle={vehicle} />*/}

          {vehicle.description && (
  <section className="vd-desc">
    <header className="vd-desc__header">
      <FaAlignLeft aria-hidden="true" />
      <h3>Descrição</h3>
    </header>
    <div className="vd-desc__body">
      <p>{vehicle.description}</p>
    </div>
  </section>
)}


          <VideoPlayer
        title={`Vídeo`}
        //youtubeId={manualTestId}
        youtubeId={vehicle.videoUrl}
          />

          



        </div>

        <aside className="vd-right">
          <span className={`vd-inventory-badge vd-inventory-badge--${inventoryType || 'default'}`}>{inventoryLabel}</span>
          <span className="vd-brand">{vehicle.brand.toUpperCase()}</span>
          <h1 className="vd-title">
            {vehicle.model} {vehicle.version}
          </h1>
            
            
          <div className="vd-price">
            {hasPrice ? formatCurrency(price) : 'Preco sob consulta'}
          </div>
          {hasFipePrice && (
            <div className="vd-fipe">
              <div className="vd-fipe__row">
                <span className="vd-fipe__label">Tabela FIPE</span>
                <span className="vd-fipe__value">{formatCurrency(fipeValue)}</span>
              </div>
              {fipeDiffText && (
                <div className={fipeDiffClass}>
                  {fipeDiffText}
                </div>
              )}
            </div>
          )}
          

          <button
           className="btn-primary vd-action"
           onClick={handleWhatsApp}
>
           Falar no WhatsApp
          </button>
          {/* <ShareButton url={window.location.href} /> */}
          {/*<FinancingSimulatorButton price={vehicle?.price} />*/}
          

          {/* Sobre este carro */}
          <IconContext.Provider value={{ size: '1.2em', className: 'spec-icon' }}>
            <ul className="specs-grid">
              {specs.map(({ Icon, label, value }) => (
                <li key={label}>
                  <Icon />
                  <div className="spec-info">
                    <span className="spec-value">{value}</span>
                    <span className="spec-label">{label}</span>
                  </div>
                </li>
              ))}
            </ul>
          </IconContext.Provider>

          

          {/* Acessórios */}
          {vehicle.options.length > 0 && (
            <div className="accessories">
              <h3>Acessórios e outros</h3>
              <ul className="accessories-list">
                {vehicle.options.map(o => (
                  <li key={o.id}>
                    <FaTools className="acc-icon" />
                    <span>{o.name}</span>
                  </li>
                ))}
              </ul>
            </div>

            
          )}

          
        </aside>
      </div>
    </div>
  )
}
